import { TemplateBadge, RevoltIcon, RevoltMagicButton } from '../../../components';
import { StatusCluster } from '../../app/components/StatusCluster';
import { RevoltButton } from '../../../components';
import { canShowRemixButton } from '../../../core/utils/appAccessUtils';
import { parseMarkdown, setupCodeBlockCopyButtons, setupExternalLinkHandlers, renderMath } from '../../../core';
import './OverviewCard.css';

import { useState, useEffect, useMemo, useRef } from 'react';
import { RevoltInput } from '../../../components/RevoltInput';
import { RevoltTextarea } from '../../../components/RevoltTextarea';

export function OverviewCard({
  app,
  onOpenWorkspace,
  onOpenPlayspace,
  onRemix,
  onRename,
  onDescriptionChange,
  canEdit,
  canShowWorkspace,
  isOwner,
  isAdmin,
}) {
  const hasApp = !!app;
  const ownerLabel = app?.ownerUsername || app?.ownerName || app?.ownerId || 'Unknown';
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingDesc, setIsEditingDesc] = useState(false);
  const [titleValue, setTitleValue] = useState(hasApp ? app.name || '' : '');
  const [originalTitle, setOriginalTitle] = useState(hasApp ? app.name || '' : '');
  const [descValue, setDescValue] = useState(hasApp ? app.description || '' : '');
  const [originalDesc, setOriginalDesc] = useState(hasApp ? app.description || '' : '');
  const canRemix = canShowRemixButton(app, { isOwner, isAdmin });
  const descriptionRef = useRef(null);

  const parsedDescription = useMemo(() => {
    const desc = app?.description?.trim();
    return desc ? parseMarkdown(desc) : '';
  }, [app?.description]);

  useEffect(() => {
    if (!isEditingDesc && descriptionRef.current && app?.description?.trim()) {
      setupCodeBlockCopyButtons(descriptionRef.current);
      setupExternalLinkHandlers(descriptionRef.current);
      renderMath(descriptionRef.current);
    }
  }, [app?.description, isEditingDesc]);

  useEffect(() => {
    if (hasApp) {
      setTitleValue(app.name || '');
      setOriginalTitle(app.name || '');
      setDescValue(app.description || '');
      setOriginalDesc(app.description || '');
    }
  }, [hasApp, app]);

  if (!hasApp) return null;

  const handleTitleClick = () => {
    if (!canEdit) return;
    setIsEditingTitle(true);
  };

  const handleTitleChange = (e) => setTitleValue(e.target.value);

  const saveTitle = async () => {
    const trimmed = (titleValue || '').trim();
    if (trimmed === originalTitle) {
      setTitleValue(originalTitle);
      setIsEditingTitle(false);
      return;
    }
    if (!trimmed) {
      setTitleValue(originalTitle);
      setIsEditingTitle(false);
      return;
    }
    if (onRename) await onRename(trimmed);
    setOriginalTitle(trimmed);
    setIsEditingTitle(false);
  };

  const handleTitleBlur = () => saveTitle();
  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveTitle();
    } else if (e.key === 'Escape') {
      setTitleValue(originalTitle);
      setIsEditingTitle(false);
    }
  };

  const handleDescClick = () => {
    if (!canEdit) return;
    setIsEditingDesc(true);
  };

  const handleDescChange = (e) => setDescValue(e.target.value);

  const saveDesc = async () => {
    const val = (descValue ?? '').trim();
    if (val === originalDesc) {
      setDescValue(originalDesc);
      setIsEditingDesc(false);
      return;
    }
    if (onDescriptionChange) await onDescriptionChange(val);
    setOriginalDesc(val);
    setIsEditingDesc(false);
  };

  const handleDescBlur = () => saveDesc();
  const handleDescKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      saveDesc();
    } else if (e.key === 'Escape') {
      setDescValue(originalDesc);
      setIsEditingDesc(false);
    }
  };

  return (
    <div className="account-card settings-card">
      <div className="card-header">
        <h2 className="card-title">
          <i className="fa-solid fa-gear"></i> Overview
        </h2>
      </div>
      <div className="card-body overview-body">
        <div className="overview-top">
          <div className="overview-title-row">
            <div className="overview-title-block">
              {isEditingTitle ? (
                <div className="overview-title-input">
                  <RevoltInput
                    value={titleValue}
                    onChange={handleTitleChange}
                    onBlur={handleTitleBlur}
                    onKeyDown={handleTitleKeyDown}
                    autoFocus
                  />
                </div>
              ) : (
                <h3
                  className={canEdit ? 'overview-title-clickable' : ''}
                  onClick={handleTitleClick}
                  title={canEdit ? 'Click to rename app' : undefined}
                >
                  {app.name}
                </h3>
              )}
              <span className="overview-by">By {ownerLabel}</span>
            </div>
            <div className="overview-cluster">
              <StatusCluster
                status='idle'
                isPublic={!!app.isPublic}
                isFavorite={!!app.isFavorited}
              />
            </div>
          </div>
        </div>

        <div className="overview-description">
          {isEditingDesc ? (
            <div className="overview-description-panel overview-description-input">
              <RevoltTextarea
                value={descValue}
                onChange={handleDescChange}
                onBlur={handleDescBlur}
                onKeyDown={handleDescKeyDown}
                placeholder="Add a description..."
                rows={4}
                minRows={4}
                maxRows={18}
                autoResize
                autoFocus
              />
            </div>
          ) : (
            <div
              className={`overview-description-panel overview-subtitle overview-description-text ${canEdit ? 'overview-description-clickable' : ''}`}
              onClick={handleDescClick}
              title={canEdit ? 'Click to edit description' : undefined}
            >
              {app.description?.trim() ? (
                <div
                  ref={descriptionRef}
                  className="overview-description-markdown"
                  dangerouslySetInnerHTML={{ __html: parsedDescription }}
                />
              ) : (
                canEdit ? 'Add a description...' : ''
              )}
            </div>
          )}
        </div>

        <div className="overview-dates-row">
          <div className="overview-date-panel">
            <span className="overview-date-label">Created</span>
            <span className="overview-date-value">{app.createdAtFormatted}</span>
          </div>
          <div className="overview-date-panel">
            <span className="overview-date-label">Modified</span>
            <span className="overview-date-value">{app.modifiedAtFormatted}</span>
          </div>
        </div>

        <div className="overview-footer">
          <div className="overview-badge">
            <TemplateBadge template={app.template} appType={app.appType} />
          </div>
          <div className="overview-actions">
            {canRemix && (
              <RevoltMagicButton
                variant="ghost"
                size="sm"
                onClick={onRemix}
                style={{ marginRight: 8 }}
              >
                <RevoltIcon name="lucide:wand-sparkles" size="sm" />
                Remix
              </RevoltMagicButton>
            )}
            <RevoltButton
              variant="ghost"
              size="sm"
              as="a"
              href={`/playspace/${app.id}`}
              data-navigation-button
              onClick={async (e) => {
                e.preventDefault();
                await onOpenPlayspace();
              }}
              style={{ marginLeft: canShowWorkspace ? 8 : 0 }}
            >
              Playspace
            </RevoltButton>
            {canShowWorkspace && (
              <RevoltButton
                variant="accent"
                size="sm"
                as="a"
                href={`/workspace/${app.id}`}
                data-navigation-button
                onClick={async (e) => {
                  e.preventDefault();
                  await onOpenWorkspace();
                }}
                style={{ marginLeft: 8 }}
              >
                Workspace
              </RevoltButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

